import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, Home, BookOpen, Settings as SettingsIcon, LogOut, 
  Search, Bell, Menu, X, Filter, ChevronDown, Play, 
  BookCopy, Atom, BriefcaseMedical, Feather, Star, CheckCircle, Clock,
  XCircle, AlertCircle
} from 'lucide-react';

// --- API Helper Functions (UPDATED) ---
const API_BASE_URL = 'https://zimsyai-backend.onrender.com'; 
const getUserId = () => {
  return localStorage.getItem('userId');
};

const API = {

  markNotificationsRead: () => {
  const userId = getUserId();
  return fetch(`${API_BASE_URL}/api/user/notifications/mark-read`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: parseInt(userId) })
  }).then(res => res.json());
  },

  getEnrolledSubjects: () => {
    const userId = getUserId();
    return fetch(`${API_BASE_URL}/api/user/enrolled-subjects?user_id=${userId}`).then(res => res.json());
  },
  getAllSubjects: () => fetch(`${API_BASE_URL}/api/subjects`).then(res => res.json()),
  getRequestedSubjects: () => {
    const userId = getUserId();
    return fetch(`${API_BASE_URL}/api/user/requested-subjects?user_id=${userId}`).then(res => res.json());
  },
  enrollInSubject: (subjectId) => {
    const userId = getUserId();
    return fetch(`${API_BASE_URL}/api/user/enroll-subject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject_id: subjectId, user_id: parseInt(userId) })
    }).then(res => res.json());
  },
  removeEnrolledSubject: (userSubjectId) => fetch(`${API_BASE_URL}/api/user/enrolled-subject/${userSubjectId}`, {
    method: 'DELETE'
  }).then(res => res.json()),
  
  // This endpoint is now called *after* successful payment
  requestSubject: (subjectName, content) => {
    const userId = getUserId();
    return fetch(`${API_BASE_URL}/api/subjects/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        subject_name: subjectName, 
        content: content, 
        user_id: parseInt(userId),
      })
    }).then(res => res.json());
  },
  submitReview: (stars, liked, improvement) => {
    const userId = getUserId();
    return fetch(`${API_BASE_URL}/api/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        stars, 
        liked, 
        improvement, 
        user_id: parseInt(userId)
      })
    }).then(res => res.json());
  },


  /**
   * [REAL] This now calls your backend's /api/user/notifications endpoint
   */
  getNotifications: () => {
    const userId = getUserId();
    if (!userId) return Promise.resolve([]); // No user, no notifications
    
    return fetch(`${API_BASE_URL}/api/user/notifications?user_id=${userId}`)
      .then(res => res.json())
      .then(data => {
        // Your backend now provides the welcome message
        if (data && Array.isArray(data)) {
          return data;
        }
        // Handle error cases where backend returns { "error": "..." }
        return []; 
      })
      .catch(err => {
        console.error("Failed to fetch notifications:", err);
        return []; // Return empty on error
      });
  }
};

// --- [NEW] Confirmation Modal Component (Unchanged) ---
const ConfirmationModal = ({ show, title, message, confirmText = "Confirm", onConfirm, onClose }) => {
  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md bg-neutral-900 rounded-2xl border border-white/10 p-6 m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-600/20">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-sm text-neutral-300 mb-6">{message}</p>
          </div>
        </div>
        
        <div className="flex justify-end gap-4">
          <button 
            onClick={onClose}
            className="rounded-lg bg-neutral-700 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-neutral-600"
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-red-700"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- [NEW] Notifications Dropdown Component (Unchanged) ---
const NotificationsDropdown = ({ notifications, onClose }) => {
  return (
    <div 
      className="absolute top-16 right-0 w-80 bg-neutral-900 border border-white/10 rounded-lg shadow-lg z-50 animate-fadeIn"
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
    >
      <div className="p-4 border-b border-white/10">
        <h4 className="text-lg font-semibold text-white">Notifications</h4>
      </div>
      <div className="flex flex-col max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="p-4 text-sm text-neutral-400">No new notifications.</p>
        ) : (
          notifications.map(notif => (
            <div key={notif.id} className={`p-4 border-b border-white/10 ${!notif.is_read ? 'bg-purple-900/20' : ''} hover:bg-neutral-800`}>
              <p className="text-sm text-neutral-200">{notif.message}</p>
              <p className="text-xs text-neutral-500 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};


// --- [NEW] Notification Component (Unchanged) ---
const Notification = ({ message, type, onClose }) => {
  const isSuccess = type === 'success';
  const baseStyle = "fixed top-5 right-5 z-50 flex items-center p-4 rounded-lg shadow-lg max-w-sm text-white";
  const typeStyle = isSuccess ? "bg-green-600" : "bg-red-600";
  const Icon = isSuccess ? CheckCircle : XCircle;

  return (
    <div className={`${baseStyle} ${typeStyle} animate-slide-in`}>
      <Icon className="h-6 w-6 mr-3" />
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-4 -mr-1 p-1 rounded-md hover:bg-white/20 transition-colors">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
};

// --- [NEW] Video URL Helper ---
// This checks for YouTube/Vimeo for iframe, or returns 'video' type for <video> tag
const getVideoPlayer = (url) => {
  if (!url) return { type: null, src: null };
  // Google Drive video support
  if (url.includes("drive.google.com")) {
    try {
      const fileId = url.split("/d/")[1].split("/")[0];
      return {
        type: "iframe",
        src: `https://drive.google.com/file/d/${fileId}/preview`
      };
    } catch (err) {
      console.error("Drive video parse failed:", err);
    }
  }

  try {
    // [FIX] Check for already-embeddable YouTube links
    if (url.includes("youtube.com/embed/")) {
      return { type: 'iframe', src: url };
    }

    // Check for standard YouTube watch links
    if (url.includes("youtube.com/watch?v=")) {
      const videoId = new URL(url).searchParams.get('v');
      return { type: 'iframe', src: `https://www.youtube.com/embed/${videoId}` };
    }
    
    // [NEW] Check for short youtu.be links
    if (url.includes("youtu.be/")) {
      const videoId = new URL(url).pathname.substring(1); // Get path and remove leading '/'
      return { type: 'iframe', src: `https://www.youtube.com/embed/${videoId}` };
    }

    // Check for Vimeo
    if (url.includes("vimeo.com/")) {
      const videoId = new URL(url).pathname.split('/').pop();
      return { type: 'iframe', src: `https://player.vimeo.com/video/${videoId}` };
    }
    
    // Check for common video file types
    if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.ogg')) {
      return { type: 'video', src: url };
    }
  } catch (error) {
    console.error("Error parsing video URL:", error);
    return { type: null, src: null }; // Fallback on URL parsing error
  }
  
  // Default: We don't know how to play this
  return { type: null, src: null };
};

// --- Main App Component (UPDATED) ---
export default function Dashboard({ setIsLoggedIn }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'addCourse', 'myCourses', 'settings'
  
  // --- Data State ---
  const [mySubjects, setMySubjects] = useState([]); // UserSubject[]
  const [allSubjects, setAllSubjects] = useState([]); // Subject[]
  const [requestedSubjects, setRequestedSubjects] = useState([]); // RequestedSubject[]
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null);


  // --- Modal State ---
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  
  // --- Confirmation Modal State (Unchanged) ---
  const [confirmationModal, setConfirmationModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    confirmText: 'Confirm', 
    onConfirm: () => {} 
  });

  // --- Search State (Unchanged) ---
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- User & Notification State (Unchanged) ---
  const [userEmail, setUserEmail] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  // --- Notification State (Unchanged) ---
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // --- Effects ---

  // Auto-hide toast notification
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Initial data load
  useEffect(() => {
    setAnimate(true); // Trigger animations
    
    // Get user email from storage
    setUserEmail(localStorage.getItem('userEmail') || '');
    
    // Fetch dashboard data
    const loadData = async () => {
      try {
        const [enrolled, all, requested, initialNotifications] = await Promise.all([
        API.getEnrolledSubjects(),
        API.getAllSubjects(),
        API.getRequestedSubjects(),       
        API.getNotifications()        // 5th result = notifications

        ]);
      

        setMySubjects(enrolled || []);

        setAllSubjects(all || []);
        setRequestedSubjects(requested || []);
        setNotifications(initialNotifications || []);
        setIsLoading(false);
        
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        setIsLoading(false);
        showNotification("Could not load dashboard data.", "error");
      }
    };
    
    loadData();

  }, []);

  // [NEW] Polling for notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      const data = await API.getNotifications();
      setNotifications(data);
    };
  
    // Fetch immediately on load (already done above), then poll
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 30000); // Poll every 30 seconds
  
    // Clear interval on component unmount
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array means this runs once on mount

  // [NEW] Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isNotificationsOpen) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isNotificationsOpen]);


  // --- Search Filtering (Unchanged) ---
  const filteredSubjects = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    
    if (!lowerSearch) {
      // No search, return original lists
      return { mySubjects: mySubjects, allSubjects: allSubjects };
    }

    if (activeView === 'addCourse') {
      // Only filter allSubjects
      return {
        mySubjects: mySubjects,
        allSubjects: allSubjects.filter(s => s.name.toLowerCase().includes(lowerSearch))
      };
    } else if (activeView === 'dashboard' || activeView === 'myCourses' || activeView === 'settings') {
        // Only filter mySubjects
      return {
        mySubjects: mySubjects.filter(us => us.subject.name.toLowerCase().includes(lowerSearch)),
        allSubjects: allSubjects
      };
    }
    // Default case
    return { mySubjects: mySubjects, allSubjects: allSubjects };
    
  }, [searchTerm, activeView, mySubjects, allSubjects]);


  // --- Handlers (Unchanged) ---

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail'); // Clear email on logout
    setIsLoggedIn(false);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  const handleEnroll = async (subjectId) => {
    if (mySubjects.find(us => us.subject.id === subjectId)) {
      showNotification("You are already enrolled in this subject.", "error");
      return;
    }
    
    try {
      const data = await API.enrollInSubject(subjectId);
      if (data.success) {
        setMySubjects(prev => [...prev, data.userSubject]);
        showNotification("Subject added successfully!", "success");
      } else {
        showNotification(data.error || "Failed to add subject.", "error");
      }
    } catch (error) {
      showNotification("An error occurred.", "error");
    }
  };

  // handleRemove now uses the modal
  const handleRemove = (userSubjectId, subjectName) => {
    setConfirmationModal({
      show: true,
      title: "Remove Subject",
      message: `Are you sure you want to remove "${subjectName}" from your courses?`,
      confirmText: "Remove",
      onConfirm: async () => {
        try {
          const data = await API.removeEnrolledSubject(userSubjectId);
          if (data.success) {
            setMySubjects(prev => prev.filter(us => us.id !== userSubjectId));
            showNotification("Subject removed.", "success");
          } else {
            showNotification(data.error || "Failed to remove subject.", "error");
          }
        } catch (error) {
          showNotification("An error occurred.", "error");
        } finally {
          setConfirmationModal({ ...confirmationModal, show: false });
        }
      },
      onClose: () => setConfirmationModal({ ...confirmationModal, show: false })
    });
  };

  // handleRequestSubmit now includes paymentId
  const handleRequestSubmit = async (subjectName, syllabus) => {
    try {
      const data = await API.requestSubject(subjectName, syllabus);
      if (data.success) {
        setRequestedSubjects(prev => [...prev, data.request]); 
        setIsRequestModalOpen(false);
        showNotification("Subject requested successfully!", "success");
      } else {
        showNotification(data.error || "Failed to submit request.", "error");
      }
    } catch (error) {
      showNotification("An error occurred.", "error");
    }
  };

  const handleReviewSubmit = async (stars, liked, improvement) => {
    try {
      const data = await API.submitReview(stars, liked, improvement);
      if (data.success) {
        setIsReviewModalOpen(false);
        showNotification("Thank you for your feedback!", "success");
      } else {
        showNotification(data.error || "Failed to submit review.", "error");
      }
    } catch (error) {
      showNotification("An error occurred.", "error");
    }
  };

  // --- Content Rendering (Unchanged) ---
  const renderView = () => {
    if (isLoading) {
      return <div className="p-8 text-center">Loading your dashboard...</div>;
    }

    switch (activeView) {
      case 'dashboard':
        if (selectedSubject) {
          return (
            <SubjectViewer
              subject={selectedSubject}
              onBack={() => setSelectedSubject(null)}
            />
          );
        }

        return (
          <>
            <HeroBanner animate={animate} hasCourses={mySubjects.length > 0} />
            <DashboardGrid
              animate={animate}
              mySubjects={filteredSubjects.mySubjects}
              onSelectSubject={setSelectedSubject}   // NEW
            />
          </>
        );

      case 'addCourse':
        return <AddCourse 
                  allSubjects={filteredSubjects.allSubjects} 
                  mySubjects={mySubjects} // Pass original for enrolled check
                  onEnroll={handleEnroll} 
                  onRequestClick={() => setIsRequestModalOpen(true)} 
                />;
      case 'myCourses':
        return <MyCourses mySubjects={filteredSubjects.mySubjects} />;
      case 'settings':
        return <Settings 
                  mySubjects={filteredSubjects.mySubjects} 
                  requestedSubjects={requestedSubjects}
                  onRemove={handleRemove}
                  onReviewClick={() => setIsReviewModalOpen(true)}
                />

      default:
        return <div>Page not found.</div>;
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-neutral-900 text-neutral-100 font-sans">
      
      {/* --- Render Notification --- */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ ...notification, show: false })}
        />
      )}
      
      {/* --- Render Confirmation Modal --- */}
      <ConfirmationModal {...confirmationModal} />

      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        setIsSidebarOpen={setIsSidebarOpen} 
        onLogout={handleLogout}
        activeView={activeView}
        setActiveView={setActiveView}
      />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Header 
          setIsSidebarOpen={setIsSidebarOpen}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setActiveView={setActiveView}
          userEmail={userEmail}
          notifications={notifications}
          setNotifications={setNotifications}   // ✅ ADD THIS
          isNotificationsOpen={isNotificationsOpen}
          setIsNotificationsOpen={setIsNotificationsOpen}
        />

        
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {/* Mobile Search Bar */}
          <div className="relative w-full mb-4 md:hidden">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={20} />
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 py-2 pl-10 pr-4 text-white placeholder-neutral-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>
          
          {renderView()}
        </main>
      </div>
      
      {/* Modals */}
      {isRequestModalOpen && (
        <RequestSubjectModal 
          onClose={() => setIsRequestModalOpen(false)}
          onSubmit={handleRequestSubmit}
          showNotification={showNotification}
        />

      )}
      {isReviewModalOpen && (
        <ReviewModal 
          onClose={() => setIsReviewModalOpen(false)}
          onSubmit={handleReviewSubmit}
        />
      )}
      
      {/* CSS Animations (unchanged) */}
      <style>{`
        /* ... [All your @keyframes and animation classes] ... */
        @keyframes slideUpWord { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUpFade { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDownFade { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes growBar { from { height: 0; } to { height: var(--bar-height); } }
        @keyframes drawLine { from { stroke-dashoffset: 150; } to { stroke-dashoffset: 0; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes progressBar { from { width: 0; } to { width: var(--progress-width); } }
        .animate-slideUpWord { animation: slideUpWord 0.8s ease-out forwards; opacity: 0; }
        .animate-slideUpFade { animation: slideUpFade 0.8s ease-out forwards; opacity: 0; }
        .animate-slideDownFade { animation: slideDownFade 0.8s ease-out forwards; opacity: 0; }
        .animate-growBar { animation: growBar 0.8s ease-out forwards; height: 0; }
        .animate-drawLine { stroke-dasharray: 150; stroke-dashoffset: 150; animation: drawLine 1.2s ease-out forwards; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-progressBar { animation: progressBar 1s ease-out forwards; width: 0; }
      `}</style>
    </div>
  );
}

// --- Sidebar (Unchanged) ---
function Sidebar({ isSidebarOpen, setIsSidebarOpen, onLogout, activeView, setActiveView }) {
  return (
    <>
      <div 
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden ${
          isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsSidebarOpen(false)}
      ></div>
      <aside 
        className={`fixed top-0 left-0 z-50 flex h-full w-64 flex-col border-r border-white/10 bg-neutral-950 transition-transform lg:w-20 lg:translate-x-0 lg:sticky lg:h-screen ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-20 items-center justify-start px-6 lg:justify-center lg:px-0 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-center w-10 h-10">
            <img src="/image/logo-png.png" alt="ZimsyAI Logo" className="h-full w-full object-contain rounded-lg" />
          </div>
        </div>
        <nav className="flex flex-1 flex-col justify-between items-start px-4 lg:items-center lg:px-0 py-6">
          <div className="flex flex-col items-start w-full lg:items-center gap-4">
            <SidebarIcon 
              icon={<Plus size={24} />} 
              tooltip="Add Course"
              active={activeView === 'addCourse'}
              onClick={() => { setActiveView('addCourse'); setIsSidebarOpen(false); }}
            />
            <SidebarIcon 
              icon={<Home size={24} />} 
              tooltip="Dashboard"
              active={activeView === 'dashboard'}
              onClick={() => { setActiveView('dashboard'); setIsSidebarOpen(false); }}
            />
            <SidebarIcon 
              icon={<BookOpen size={24} />} 
              tooltip="My Courses" 
              active={activeView === 'myCourses'}
              onClick={() => { setActiveView('myCourses'); setIsSidebarOpen(false); }}
            />
            <SidebarIcon 
              icon={<SettingsIcon size={24} />} 
              tooltip="Settings" 
              active={activeView === 'settings'}
              onClick={() => { setActiveView('settings'); setIsSidebarOpen(false); }}
            />
          </div>
          
          <div className="flex flex-col items-start w-full lg:items-center gap-4">
            <SidebarIcon icon={<LogOut size={24} />} tooltip="Log Out" onClick={onLogout} />
          </div>
        </nav>
        <button 
          className="absolute top-5 right-4 text-neutral-400 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        >
          <X size={24} />
        </button>
      </aside>
    </>
  );
}

// --- SidebarIcon (Unchanged) ---
function SidebarIcon({ icon, tooltip, active = false, onClick }) {
  return (
    <div className="group relative">
      <button onClick={onClick}
        className={`flex items-center gap-4 w-full h-12 rounded-xl transition-all ${
          active 
            ? 'text-white' // Only set the text color on the button
            : 'text-neutral-500 hover:bg-neutral-800 hover:text-neutral-100'
        } lg:w-12 lg:h-12 lg:justify-center lg:gap-0`}
      >
        <div className={`flex h-12 w-12 items-center justify-center flex-shrink-0 transition-all lg:rounded-xl ${
          active ? 'bg-purple-600' : '' // Apply bg to this div
        }`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-100 lg:hidden">{tooltip}</span>
      </button>
      <span className="absolute left-full top-1/2 ml-4 -translate-y-1/2 scale-0 rounded bg-neutral-800 px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-all group-hover:scale-100 group-hover:opacity-100 hidden lg:block">
        {tooltip}
      </span>
    </div>
  );
}

// --- Header (UPDATED) ---
function Header({ 
  setIsSidebarOpen, 
  searchTerm, 
  setSearchTerm, 
  setActiveView, 
  userEmail,
  notifications,
  setNotifications,   // ✅ ADD THIS
  isNotificationsOpen,
  setIsNotificationsOpen 
}) {

  
  const userInitial = userEmail ? userEmail[0].toUpperCase() : 'U';
  
  // [REAL] Count is now based on data
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <header className="flex h-20 flex-shrink-0 items-center justify-between border-b border-white/10 px-4 md:px-6 lg:px-8">
      <div className="flex items-center gap-4">
        <button 
          className="text-neutral-400 lg:hidden"
          onClick={() => setIsSidebarOpen(true)}
        >
          <Menu size={24} />
        </button>
        <div className="flex items-center gap-3">
          <span className="hidden text-2xl font-bold text-white sm:block">ZimsyAI</span>
        </div>
      </div>
      <div className="flex items-center gap-4 md:gap-6">
        {/* Search Bar */}
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={20} />
          <input 
            type="text" 
            placeholder="Search..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-neutral-700 bg-neutral-800 py-2 pl-10 pr-4 text-white placeholder-neutral-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        </div>
        
        {/* Bell Icon */}
        <div className="relative">
          <button 
            className="relative text-neutral-400 hover:text-white"
            onClick={async (e) => {
                e.stopPropagation();
                
                // If opening the dropdown → mark notifications read
                if (!isNotificationsOpen) {
                  await API.markNotificationsRead();
                  const updated = await API.getNotifications();
                  setNotifications(updated);
                }

                setIsNotificationsOpen(!isNotificationsOpen);
              }}

          >
            <Bell size={22} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-[10px] font-bold text-white">
                {unreadCount}
              </span>
            )}
          </button>
          {isNotificationsOpen && <NotificationsDropdown notifications={notifications} onClose={() => setIsNotificationsOpen(false)} />}
        </div>
        
        {/* User Avatar */}
        <button 
          onClick={() => setActiveView('settings')}
          className="h-10 w-10 flex items-center justify-center overflow-hidden rounded-full border-2 border-neutral-700 bg-purple-800 text-lg font-semibold text-white hover:border-purple-500"
        >
          {userInitial}
        </button>
      </div>
    </header>
  );
}

// --- Hero Banner (Unchanged) ---
function HeroBanner({ animate, hasCourses }) {
  const words = hasCourses 
    ? ['Welcome', 'back,', 'ready', 'to', 'learn?'] 
    : ['Start', 'your', 'learning', 'journey', 'today.'];
  
  const heading = hasCourses 
    ? "Let's pick up where you left off."
    : "No course is taken";
  
  return (
    <div className="relative mb-6 w-full overflow-hidden rounded-2xl bg-neutral-800 p-6 md:p-8">
      <img 
        src="https://placehold.co/1200x300/1E1B2E/663399?text=ZimsyAI" 
        alt="ZimsyAI Banner" 
        className="absolute inset-0 h-full w-full object-cover opacity-30"
        onError={(e) => { e.target.src = 'https://placehold.co/1200x300/1E1B2E/3D3A50?text=ZimsyAI'; }}
      />
      
      <div className="relative z-10 flex flex-col items-start">
        <span className="mb-4 text-sm font-medium text-purple-300">Your Dashboard</span>
        <h2 className="mb-6 text-2xl font-bold text-white md:text-3xl leading-tight">
          {hasCourses ? (
            words.map((word, i) => (
              <span
                key={i}
                className={`inline-block mr-3 ${animate ? 'animate-slideUpWord' : ''}`}
                style={{
                  animationDelay: `${i * 0.15}s`,
                  opacity: animate ? undefined : 1
                }}
              >
                {word}
              </span>
            ))
          ) : (
            <span 
              className={`inline-block ${animate ? 'animate-slideUpWord' : ''}`}
              style={{ opacity: animate ? undefined : 1 }}
            >
              {heading}
            </span>
          )}
        </h2>
        
        {!hasCourses && (
          <p className={`text-neutral-300 ${animate ? 'animate-slideUpWord' : ''}`} style={{ animationDelay: '0.5s', opacity: animate ? undefined : 1 }}>
            Add a course from the 'Add Course' tab to get started.
          </p>
        )}
      </div>
    </div>
  );
}

// --- Dashboard Grid (Unchanged) ---
function DashboardGrid({ animate, mySubjects, onSelectSubject }) {

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <CourseList animate={animate} mySubjects={mySubjects} onSelectSubject={onSelectSubject} />
      </div>
      <div className="flex flex-col gap-6">
        <MyProgress animate={animate} mySubjects={mySubjects} />
      </div>
    </div>
  );
}

// --- Course List (Unchanged) ---
function CourseList({ animate, mySubjects, onSelectSubject }) {
  const getIcon = (section) => {
    switch (section.toLowerCase()) {
      case 'nptel': return <BookCopy size={24} className="text-blue-400" />;
      case 'college core': return <Atom size={24} className="text-purple-400" />;
      case 'first aid': return <BriefcaseMedical size={24} className="text-red-400" />;
      default: return <Feather size={24} className="text-yellow-400" />;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">Course you're taking</h3>
        <button className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white">
          <Filter size={16} />
          Filter by
          <ChevronDown size={16} />
        </button>
      </div>
      
      <div className="flex flex-col gap-4">
        {mySubjects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/20 bg-neutral-950 p-8 text-center text-neutral-400">
            <p>No course taken</p>
            <p className="text-sm">Click the 'Add Course' icon to enroll in a subject.</p>
          </div>
        ) : (
          mySubjects.map(({ subject }, index) => (
          <CourseCard
            key={subject.id}
            course={{
              icon: getIcon(subject.section),   // FIXED
              title: subject.name,              // FIXED
              subjectObj: subject               // store original for later use
            }}
            index={index}
            animate={animate}
            onClick={() => onSelectSubject(subject)}
          />
        ))


        )}
      </div>
    </div>
  );
}

// --- CourseCard (Unchanged) ---
function CourseCard({ course, index, animate, onClick }) {
  const isCompleted = course.status === 'Completed';
  
  return (
    <div onClick={onClick}
      className={`flex flex-col gap-4 rounded-2xl border border-white/10 bg-neutral-950 p-4 transition-all hover:border-purple-500/50 md:flex-row md:items-center md:gap-6 ${animate ? 'animate-slideUpFade' : ''}`}
      style={{ 
        animationDelay: `${0.5 + index * 0.15}s`,
        opacity: animate ? undefined : 1
      }}
    >
      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-neutral-800">
        {course.icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-lg font-semibold text-white truncate">{course.title}</h4>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-400">
        </div>
      </div>
      <div className="w-full md:w-56">
        <div className="mb-1 flex items-center justify-between">
        </div>
      </div>
    </div>
  );
}

// --- Progress Section (Unchanged) ---
function MyProgress({ animate, mySubjects }) {
  const hoursSpent = 0; 
  const coursesTaken = mySubjects.length;
  
  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">My Progress</h3>
        <button className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white">
          Last month
          <ChevronDown size={16} />
        </button>
      </div>

      <div 
        className={`rounded-2xl border border-white/10 bg-neutral-950 p-6 ${animate ? 'animate-slideDownFade' : ''}`}
        style={{ 
          animationDelay: '0.95s',
          opacity: animate ? undefined : 1
        }}
      >
        <span className="text-sm text-neutral-400">Courses taken</span>
        <p className="mt-1 text-4xl font-bold text-white">{coursesTaken}</p>
        
      </div>
    </>
  );
}

// --- Add Course Page (Unchanged) ---
function AddCourse({ allSubjects, mySubjects, onEnroll, onRequestClick }) {
  const enrolledSubjectIds = new Set(mySubjects.map(us => us.subject.id));

  // Note: We use allSubjects which is *already filtered* by the search
  const subjectsBySection = allSubjects.reduce((acc, subject) => {
    const section = subject.section || 'Other';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(subject);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-3xl font-bold text-white mb-6">Add Free Subjects</h2>
        <div className="mb-8 rounded-xl border border-purple-500/40 bg-purple-900/20 p-4 text-center">
          <p className="text-purple-200 text-sm font-medium">
            🎉 New free subjects are added every <span className="font-semibold text-white">5 days</span> —
            keep checking this section for the latest additions!
          </p>
        </div>
      </div>

      {Object.keys(subjectsBySection).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/20 bg-neutral-950 p-8 text-center text-neutral-400">
          <p>No free subjects match your search.</p>
        </div>
      ) : (
        Object.entries(subjectsBySection).map(([section, subjects]) => (
          <div key={section} className="flex flex-col gap-4">
            <h3 className="text-xl font-semibold text-white">{section}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {subjects.map(subject => {
                const isEnrolled = enrolledSubjectIds.has(subject.id);
                return (
                  <div 
                    key={subject.id}
                    className="flex flex-col justify-between gap-4 rounded-2xl border border-white/10 bg-neutral-950 p-6 transition-all hover:border-purple-500/50"
                  >
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">{subject.name}</h4>
                      <div className="text-neutral-400 text-sm mt-2">
                        {subject.remarks
                          ? subject.remarks.split("\n").map((line, i) => (
                              <p key={i} className="flex items-start gap-2 mb-1">
                                <span className="text-purple-400">•</span>
                                <span>{line.trim()}</span>
                              </p>
                            ))
                          : <p>Basic syllabus coverage provided.</p>
                        }
                      </div>


                    </div>
                    <button 
                      onClick={() => onEnroll(subject.id)}
                      disabled={isEnrolled}
                      className={`mt-4 w-full flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-all ${
                        isEnrolled
                          ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {isEnrolled ? (
                        <>
                          <CheckCircle size={16} />
                          Added
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Add Subject
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Request Subject Section */}
      <div className="mt-8 rounded-2xl border border-purple-500/50 bg-neutral-950 p-6 text-center">

  <h3 className="text-xl font-semibold text-white mb-2">
    Couldn't find your subject?
  </h3>

  {/* Price with strike-through */}
  <p className="text-neutral-300 mb-4 flex items-center justify-center gap-2 text-lg">
      Request your custom subject and we will add it to our processing queue.
  </p>

  <button 
    onClick={onRequestClick}
    className="rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-neutral-900 transition-transform hover:scale-105"
  >
    Request a Subject
  </button>

  {/* Additional syllabus quality info */}
  <p className="text-xs text-neutral-400 mt-4 leading-relaxed">
    For the best one-shot generation quality, please provide a 
    <span className="text-purple-300"> detailed syllabus </span>
    instead of a short list.
  </p>

</div>

    </div>
  );
}

function RequestSubjectModal({ onClose, onSubmit, showNotification }) {
  const [subjectName, setSubjectName] = useState('');
  const [syllabus, setSyllabus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subjectName || !syllabus) {
      showNotification("Please fill out both fields.", "error");
      return;
    }

    setIsSubmitting(true);
    await onSubmit(subjectName, syllabus);
    setIsSubmitting(false);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-lg bg-neutral-900 rounded-2xl border border-white/10 p-6 m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">Request a New Subject</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="subjectName" className="block text-sm font-medium text-neutral-300 mb-1">Subject Name</label>
            <input 
              type="text"
              id="subjectName"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              placeholder="e.g., Quantum Computing"
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-white"
              required
            />
          </div>
          
          <div>
            <label htmlFor="syllabus" className="block text-sm font-medium text-neutral-300 mb-1">Detailed Syllabus</label>
            <textarea
              id="syllabus"
              rows="6"
              value={syllabus}
              onChange={(e) => setSyllabus(e.target.value)}
              placeholder="Paste your syllabus here..."
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-white"
              required
            />
          </div>

          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </div>
    </div>
  );
}

// --- My Courses Page (Unchanged) ---
function MyCourses({ mySubjects }) {
  const [selectedSubject, setSelectedSubject] = useState(null); 

  if (selectedSubject) {
    return (
      <SubjectViewer 
        subject={selectedSubject}
        onBack={() => setSelectedSubject(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-3xl font-bold text-white mb-6">My Courses</h2>
      </div>
      
      {mySubjects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/20 bg-neutral-950 p-8 text-center text-neutral-400">
          <p>You are not enrolled in any courses.</p>
          <p className="text-sm">Click the 'Add Course' icon to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {mySubjects.map(({ subject }) => (
            <div 
              key={subject.id}
              className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 p-6 transition-all hover:border-purple-500/50"
            >
              <div className="mb-4">
                <span className="text-sm font-medium text-purple-300 mb-2 block">{subject.section}</span>
                <h3 className="text-2xl font-semibold text-white truncate">{subject.name}</h3>
              </div>
              <button 
                onClick={() => setSelectedSubject(subject)}
                className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-neutral-900 transition-transform hover:scale-105"
              >
                <Play size={16} className="fill-neutral-900" />
                Start Learning
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Subject Viewer (UPDATED) ---
function SubjectViewer({ subject, onBack }) {
  const videoRef = React.useRef(null);

  // Parse syllabus lines
  const syllabusList = subject?.syllabus
    ? subject.syllabus.split("\n").filter(line => line.trim() !== "")
    : [];

  // Converts "03:12" or "01:02:30" into seconds
  const goToTime = (timestamp) => {
    if (!videoRef.current) return;

    const parts = timestamp.split(":").map(Number);
    let totalSeconds = 0;

    if (parts.length === 2) totalSeconds = parts[0] * 60 + parts[1];
    if (parts.length === 3) totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];

    videoRef.current.currentTime = totalSeconds;
    videoRef.current.play();
  };

  return (
    <div className="flex flex-col gap-6">

      <button 
        onClick={onBack} 
        className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white self-start"
      >
        <ChevronDown size={16} className="rotate-90" />
        Back to My Courses
      </button>

      {/* MAIN LAYOUT */}
      {/* MAIN LAYOUT - mobile first: stacked, horizontal on md+ */}
<div className="flex flex-col md:flex-row gap-6 w-full">

  {/* LEFT SIDEBAR (responsive widths + sticky on md+) */}
<aside className="w-full md:w-1/3 lg:w-1/4 bg-neutral-950 rounded-xl border border-white/10 p-4 
                 md:h-[85vh] md:overflow-y-auto md:sticky md:top-20">

    <h3 className="text-lg font-bold text-white mb-4">
      📘 Syllabus covered in this course
    </h3>

    {syllabusList.length === 0 && (
      <p className="text-neutral-500 text-sm">No syllabus available.</p>
    )}

    <div className="flex flex-col gap-2">
      {syllabusList.map((line, i) => {
        const normalized = line.replace(/\r/g, '').trim();
        const match = normalized.match(/^(\d{1,3}:\d{2}(?::\d{2})?)\s*(.*)$/);

        if (!match) {
          return (
            <p key={i} className="text-neutral-300 text-sm mb-1 break-words">
              {line}
            </p>
          );
        }

        const [, time, title] = match;

        return (
          <button
            key={i}
            onClick={() => goToTime(time)}
            className="w-full text-left rounded-lg bg-neutral-800 hover:bg-purple-700 transition p-2 flex items-start gap-3"
          >
            <span className="font-mono text-purple-300 text-xs flex-shrink-0">{time}</span>
            <span className="text-white text-sm truncate break-words max-w-[calc(100% - 64px)]">{title}</span>
          </button>
        );
      })}
    </div>
  </aside>

        {/* RIGHT CONTENT */}
        <div className="flex-1 flex flex-col gap-6">

          {/* VIDEO PLAYER */}
          <div className="w-full aspect-video rounded-xl overflow-hidden border border-white/10 bg-neutral-900">
            {(() => {
              const video = getVideoPlayer(subject.video_url);

              if (video.type === "iframe") {
                return (
                  <iframe
                    src={video.src}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                );
              }

              if (video.type === "video") {
                return (
                  <video
                    ref={videoRef}
                    src={video.src}
                    controls
                    className="w-full h-full"
                  />
                );
              }

              return (
                <div className="w-full h-full flex items-center justify-center text-neutral-400">
                  Cannot play this video URL
                </div>
              );
            })()}
          </div>

          {/* NOTES + EXTRA MATERIAL IN SIDE-BY-SIDE LAYOUT */}
<div className="flex flex-col lg:flex-row gap-6 w-full">

  {/* LEFT BOX → PDF Notes */}
  <div className="flex-1 rounded-xl bg-neutral-950 border border-white/10 p-5 flex flex-col gap-3">

    <h3 className="text-lg font-bold text-white flex items-center gap-2">
      📄 Notes (PDF)
    </h3>

    <p className="text-neutral-400 text-sm leading-relaxed">
      Quick revision notes from the one-shot video
    </p>

    <button
      onClick={() => window.open(subject.pdf_url, "_blank")}
      className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white 
                 px-4 py-2 rounded-lg text-sm font-semibold transition w-fit"
    >
      📘 Open Notes
    </button>
  </div>

  {/* RIGHT BOX → Extra Material */}
  <div className="flex-1 rounded-xl bg-neutral-950 border border-white/10 p-5 flex flex-col gap-4">

    <h3 className="text-lg font-bold text-white flex items-center gap-2">
      📦 Extra Material
    </h3>

    {subject.extra_material?.extra_note && (
      <a
        href={subject.extra_material.extra_note}
        target="_blank"
        className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 
                   text-white px-4 py-2 rounded-lg text-sm font-medium transition w-fit"
      >
        📝 Extra Notes
      </a>
    )}

    {subject.extra_material?.important_question && (
      <a
        href={subject.extra_material.important_question}
        target="_blank"
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 
                   text-white px-4 py-2 rounded-lg text-sm font-medium transition w-fit"
      >
        ❓ Important Question
      </a>
    )}

    {subject.extra_material?.assignment && (
      <a
        href={subject.extra_material.assignment}
        target="_blank"
        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 
                   text-white px-4 py-2 rounded-lg text-sm font-medium transition w-fit"
      >
        📂 Assignment
      </a>
    )}
  </div>

</div>


        </div>
      </div>
    </div>
  );
}


// --- PDF Notes Modal (Unchanged) ---
function NotesModal({ pdfUrl, onClose }) {
  
  useEffect(() => {
    // This effect cleanup has a lint warning, but it's safe.
    // We can fix it just to be clean.
    const body = document.body;
    const handleContext = (e) => e.preventDefault();
    body.addEventListener('contextmenu', handleContext);
    
    return () => {
      body.removeEventListener('contextmenu', handleContext);
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl h-[90vh] bg-neutral-900 rounded-2xl border border-white/10 flex flex-col m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white">Course Notes</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <iframe 
            src={`${pdfUrl}#toolbar=0&navpanes=0`} 
            title="Course Notes"
            width="100%"
            height="100%"
            className="border-none"
            onContextMenu={(e) => e.preventDefault()}
          >
            <p>Your browser does not support embedded PDFs.</p>
          </iframe>
        </div>
      </div>
    </div>
  );
}

// --- Settings Page (UPDATED) ---
function Settings({ mySubjects, requestedSubjects, onRemove, onReviewClick }){
  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-3xl font-bold text-white mb-6">Settings</h2>
      </div>

      {/* Taken Subjects */}
      <div className="flex flex-col gap-4">
        <h3 className="text-xl font-semibold text-white">My Enrolled Subjects</h3>
        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-4">
          {mySubjects.length === 0 ? (
            <p className="text-neutral-400 p-4 text-center">You are not enrolled in any subjects.</p>
          ) : (
            <ul className="divide-y divide-white/10">
              {mySubjects.map(userSubject => (
                <li key={userSubject.id} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-white font-medium">{userSubject.subject.name}</p>
                    <p className="text-sm text-neutral-400">{userSubject.subject.section}</p>
                  </div>
                  <button 
                    onClick={() => onRemove(userSubject.id, userSubject.subject.name)} // Pass name to modal
                    className="rounded-lg bg-red-600/20 px-4 py-2 text-sm font-medium text-red-400 transition-all hover:bg-red-600/40"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Requested Subjects */}
      <div className="flex flex-col gap-4">
        <h3 className="text-xl font-semibold text-white">My Requested Subjects</h3>
        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-4">
          {requestedSubjects.length === 0 ? (
            <p className="text-neutral-400 p-4 text-center">You have not requested any subjects.</p>
          ) : (
            <ul className="divide-y divide-white/10">
              {requestedSubjects.map(req => (
                <li key={req.id} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-white font-medium">{req.subject_name}</p>
                    <p className="text-sm text-neutral-400 break-words line-clamp-2">
                        Syllabus: {req.content}
                    </p>

                  </div>
                  <span className={`inline-flex items-center justify-center flex-shrink-0 rounded-full px-3 py-1 text-xs
                    ${req.status === "completed" ? 'bg-green-600 text-white' : 'bg-red-600/20 text-red-300'}`}
                    title={req.status}
                    style={{ minWidth: 64 }}
                  >
                    <span className="truncate max-w-[120px] block">{req.status}</span>
                  </span>


                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Review Section */}
      <div className="flex flex-col gap-4">
        <h3 className="text-xl font-semibold text-white">Help Us Improve</h3>
        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-6 text-center">
          <h4 className="text-lg font-semibold text-white mb-2">Enjoying ZimsyAI?</h4>
          <p className="text-neutral-300 mb-4">Leave us a review and let us know what you think!</p>
          <button 
            onClick={onReviewClick}
            className="rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-neutral-900 transition-transform hover:scale-105"
          >
            Leave a Review
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Review Modal (FIXED) ---
function ReviewModal({ onClose, onSubmit }) {
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [liked, setLiked] = useState('');
  const [improvement, setImprovement] = useState('');
  
  // [THE FIX] This was 'newOnCloseSubmit(false)'
  const [isSubmitting, setIsSubmitting] = useState(false); 
  
  const [error, setError] = useState(''); // Local error for validation

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stars === 0) {
      setError("Please select a star rating."); // Show local error
      return;
    }
    setError(''); // Clear error
    setIsSubmitting(true);
    await onSubmit(stars, liked, improvement);
    setIsSubmitting(false);
    // Main component will show notification and close modal
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-lg bg-neutral-900 rounded-2xl border border-white/10 p-6 m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">Leave a Review</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">Your Rating</label>
            <div 
              className="flex items-center gap-1"
              onMouseLeave={() => setHoverStars(0)}
            >
              {[1, 2, 3, 4, 5].map(index => (
                <Star 
                  key={index}
                  size={32}
                  className={`cursor-pointer transition-colors ${
                    (hoverStars || stars) >= index 
                    ? 'text-yellow-400 fill-yellow-400' 
                    : 'text-neutral-600'
                  }`}
                  onClick={() => setStars(index)}
                  onMouseEnter={() => setHoverStars(index)}
                />
              ))}
            </div>
            {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
          </div>

          <div>
            <label htmlFor="liked" className="block text-sm font-medium text-neutral-300 mb-1">What did you like?</label>
            <textarea
              id="liked"
              rows="4"
              value={liked}
              onChange={(e) => setLiked(e.target.value)}
              placeholder="What went well? What's your favorite feature?"
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-white placeholder-neutral-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>
          
          <div>
            <label htmlFor="improvement" className="block text-sm font-medium text-neutral-300 mb-1">What can we improve?</label>
            <textarea
              id="improvement"
              rows="4"
              value={improvement}
              onChange={(e) => setImprovement(e.target.value)}
              placeholder="What's missing? What could be better?"
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-white placeholder-neutral-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>
          
          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-purple-700 disabled:opacity-50"
          >
            {isSubmitting ? "Submitting..." : "Submit Review"}
          </button>
        </form>
      </div>
    </div>
  );
}

